/**
 * THE APP, AS DATA (client half).
 *
 * This file is the whole client-side answer to "what does this product mount?".
 * Adding a kernel feature is one line in `appComposition`; removing it is
 * deleting that line.
 *
 * KidGames does NOT compose the activity feature. It has no `activity` table,
 * no endpoint serving its history and no sync rule mirroring it. The absence is
 * declared here and in `infra/fleet/registry/repos.ts`.
 */
import type { MessageDescriptor } from "@lingui/core";
import type { Accessor, Component } from "solid-js";
import { composition, type Composition, type PlatformTarget, plugged } from "@fleet/kernel";
import { browserDelivery } from "@fleet/export";
import { exportPlugin, tauriDelivery } from "@fleet/plugins/export";

interface AppNavItem {
  id: string;
  to: string;
  order: number;
  label: MessageDescriptor;
  icon: Component<{ class?: string }>;
}

export type AppContribution =
  | { kind: "nav"; item: AppNavItem }
  | {
      kind: "command";
      command: { id: string; titleKey: string };
      binding?: { command: string; keys: string };
    };

export interface HostServices {
  translate: (message: MessageDescriptor) => string;
  db: Accessor<unknown>;
  drizzle: Accessor<unknown>;
  userId: Accessor<string | undefined>;
  onHistoryError: () => void;
}

export const pluginRoutes = [] as const;

export const NO_HOST: HostServices = {
  translate: () => {
    throw new Error("composition read without a host: routes pass only");
  },
  db: () => null,
  drizzle: () => null,
  userId: () => undefined,
  onHistoryError: () => {
    throw new Error("composition read without a host: routes pass only");
  },
};

export function appComposition(
  host: HostServices,
  target: PlatformTarget,
): Composition<AppContribution> {
  void host;
  return composition<AppContribution>({ name: "melodimals", target }, [
    plugged(exportPlugin, { browser: browserDelivery, fileSystem: tauriDelivery }),
  ]);
}
